import { useQueryClient } from '@tanstack/react-query';
import { customerSchema, RECEPTOR_CONDITION_LABELS, RECEPTOR_CONDITIONS, type CustomerDTO, type ReceptorCondition } from '@bifeai/shared';
import { Button, Callout, Dialog, Field, Input, Select, Textarea } from '@bifeai/ui';
import { useState, type FormEvent } from 'react';
import { api, errorMessage, isApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import { centsToPesosInput, pesosToCents } from '../../lib/units';

/** Alta o edición de un cliente. El límite de fiado solo lo ve y lo cambia quien tiene permiso. */
export function CustomerFormDialog({
  customer,
  onClose,
  onSaved,
}: {
  customer?: CustomerDTO;
  onClose: () => void;
  onSaved: (c: CustomerDTO) => void;
}) {
  const { can, me } = useSession();
  const fiscal = me.tenant.modules.includes('ARCA');
  const qc = useQueryClient();
  const manage = can('customers.manage');
  const [f, setF] = useState({
    name: customer?.name ?? '',
    phone: customer?.phone ?? '',
    docNumber: customer?.docNumber ?? '',
    ivaCondition: (customer?.ivaCondition ?? '') as ReceptorCondition | '',
    address: customer?.address ?? '',
    notes: customer?.notes ?? '',
    limit: customer ? centsToPesosInput(customer.creditLimitCents) : '0',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    const parsed = customerSchema.safeParse({
      name: f.name,
      phone: f.phone,
      docNumber: f.docNumber,
      ...(fiscal ? { ivaCondition: f.ivaCondition || null } : {}),
      address: f.address,
      notes: f.notes,
      ...(manage ? { creditLimitCents: pesosToCents(f.limit || '0') ?? -1 } : {}),
    });
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const i of parsed.error.issues) errs[String(i.path[0])] = i.message;
      return setErrors(errs);
    }
    setErrors({});
    setBusy(true);
    setError(null);
    try {
      const saved = customer
        ? await api.patch<CustomerDTO>(`/customers/${customer.id}`, parsed.data)
        : await api.post<CustomerDTO>('/customers', parsed.data);
      await qc.invalidateQueries({ queryKey: ['customers'] });
      onSaved(saved);
    } catch (err) {
      if (isApiError(err) && err.fields) setErrors(err.fields);
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()} title={customer ? `Editar ${customer.name}` : 'Nuevo cliente'} wide>
      <form className="bf-form" onSubmit={submit} noValidate>
        {error && <Callout tone="danger">{error}</Callout>}
        <Field label="Nombre y apellido" error={errors.name}>
          <Input value={f.name} onChange={set('name')} autoFocus placeholder="Juan Pérez" />
        </Field>
        <div className="bf-form-row">
          <Field label="Teléfono / WhatsApp" hint="Para mandarle el saldo por WhatsApp." error={errors.phone}>
            <Input value={f.phone} onChange={set('phone')} inputMode="tel" placeholder="11 5555-5555" />
          </Field>
          <Field label="DNI o CUIT" hint="Opcional." error={errors.docNumber}>
            <Input value={f.docNumber} onChange={set('docNumber')} inputMode="numeric" />
          </Field>
          {fiscal && (
            <Field
              label="Condición frente al IVA"
              hint="Para facturarle. Inscripto o monotributo: factura A con su CUIT."
              error={errors.ivaCondition}
            >
              <Select value={f.ivaCondition} onChange={set('ivaCondition')}>
                <option value="">Consumidor final</option>
                {RECEPTOR_CONDITIONS.filter((c) => c !== 'CF').map((c) => (
                  <option key={c} value={c}>
                    {RECEPTOR_CONDITION_LABELS[c]}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>
        <Field label="Dirección" hint="Opcional, para envíos." error={errors.address}>
          <Input value={f.address} onChange={set('address')} />
        </Field>
        {manage ? (
          <Field
            label="Límite de fiado"
            hint="Hasta cuánto puede deber. En $ 0 no se le fía sin autorización (un dueño o encargado lo autoriza con su PIN)."
            error={errors.creditLimitCents}
          >
            <Input value={f.limit} onChange={set('limit')} inputMode="decimal" placeholder="$ 0" style={{ maxWidth: 220 }} />
          </Field>
        ) : (
          !customer && <Callout>El límite de fiado lo pone el dueño o el encargado. Hasta entonces, para fiarle hace falta su autorización.</Callout>
        )}
        <Field label="Notas" error={errors.notes}>
          <Textarea rows={2} value={f.notes} onChange={set('notes')} placeholder="Ej.: paga los viernes" />
        </Field>
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {customer ? 'Guardar' : 'Crear cliente'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
